import React, { useState, useEffect, useCallback } from "react";
import PdpCheckoutTemplate from "../../sections/pdp-checkout-template";
import AccordionWrapper from "../../elements/accordion-wrapper";
import PersonalInformationAccordion from "../../sections/personal-information-accordion";
import DeliveryDetailsAccordion from "../../sections/delivery-details-accordion";
import PdpCardInfo from "../../sections/pdp-card-info";
import TrustSeals from "../../sections/trust-seals";
import { appendQuery } from "../../utils/urlHelper";
import api from "../../utils/api";
import { getProductByOrderPreview } from "../../utils/measurements";
import { getPdpOrderType, getQuery, isEmptyObject, genderTypeMapping } from "../../utils/siteUtils";
import { _3dsStatus, orderTypes, ProductType, stockTypes, siteErrorCodes } from "../../utils/lynkco-constants";
import { S_GEAR_SAVE_PAYMENT, webStorage } from "../../utils/webStorage";
import { useSelector, useDispatch } from "react-redux";
import {
  pdpBillingSelector,
  pdpBundleSelector,
  pdpDeliverySelector,
  pdpLccSelector,
  pdpShippingSelector,
  pdpSummarySelector,
  pdpTouchPointSelector,
  pdpPersonalInfoSelector,
  pdpPlanTypeSelector,
  pdpLccIdSelector,
  pdpIsLoanSelector,
  pdpOrderPreviewSelector,
  pdpRiskCheckSelector
} from "../../selectors/pdp";
import { useQueryParams } from "../../utils/useQuery";
import { getMarketCountry } from "../../services/siteService";
import CheckoutErrorView from "../../sections/checkout-error-view";
import { useErrorView } from "../../hooks/useErrorView";
import CheckoutPaymentFrame from "../../sections/checkout-payment-frame";
import { selectUserState } from "../../selectors/application";
import { setProgressBarDataAction, checkoutProgressAction } from "../../redux/actions/actions";
import { formatProgressBarSteps } from "../../utils/progressBar";
import { getUserIdentity } from "../../services/userService";
import { checkPdpStock } from "../../services/pdpService";
import { setPdpRiskCheckAction, setPdpStockAction } from "../../redux/actions";
import { useDictionaryOptions } from "../../hooks/useDictionaryOptions";
import { dictTypes } from "../../utils/lynkco-constants";

const PdpPayment = ({ fields, lang }) => {
  /*Redux start*/
  const orderPreview = useSelector(pdpOrderPreviewSelector);
  const dispatch = useDispatch();
  const user = useSelector(selectUserState);
  const shippingData = useSelector(pdpShippingSelector);
  const personalInfoData = useSelector(pdpPersonalInfoSelector);
  const billingData = useSelector(pdpBillingSelector);
  const pdpSummary = useSelector(pdpSummarySelector);
  const pdpIsLoan = useSelector(pdpIsLoanSelector);
  const pdpDelivery = useSelector(pdpDeliverySelector);
  const pdpBundle = useSelector(pdpBundleSelector);
  const pdpLcc = useSelector(pdpLccSelector);
  const pdpLccId = useSelector(pdpLccIdSelector);
  const pdpTouchPoint = useSelector(pdpTouchPointSelector);
  const pdpPlanType = useSelector(pdpPlanTypeSelector);
  const pdpRiskCheck = useSelector(pdpRiskCheckSelector);
  const [genderList = []] = useDictionaryOptions([dictTypes.gender, dictTypes.idType]);

  const updateProgressBar = payload => dispatch(checkoutProgressAction(payload));
  const setPdpStock = payload => dispatch(setPdpStockAction(payload));
  const setPdpRiskCheck = payload => dispatch(setPdpRiskCheckAction(payload));
  const setProgressBarData = data => dispatch(setProgressBarDataAction(data));
  /*Redux end*/
  const { template, cardInfo, deliveryHeadingType, deliveryHeadingBody, isDeliveryOpen, trustSealsImage, isPersonalInfoOpen, personalHeadingType, personalHeadingBody } = fields;
  const { confirmationLink } = template?.fields || {};
  let [code, relatedOrderNo, lid, lno, orderNo, orderId] = useQueryParams("code", "relatedOrderNo", "lid", "lno", "orderNo", "orderId");
  const [iframeUrl, setIframeUrl] = useState(null);
  const { handleError, errorViewProps } = useErrorView();
  const [showSsn, setShowSsn] = useState(false);
  const [measurementIsSent, setMeasurementIsSent] = useState(false);

  const templateProps = {
    fields,
    className: "start-of-sales-checkout",
    bodyClass: "start-of-sales-body"
  };

  const trustSeals = trustSealsImage.value?.src
    ? [
        {
          url: trustSealsImage.value.src
        }
      ]
    : [];
  const personalInfoAccordionProps = {
    heading: {
      type: personalHeadingType,
      html: personalHeadingBody,
      typographyClass: "type-link"
    },
    open: isPersonalInfoOpen.value
  };
  const deliveryAccordionProps = {
    heading: {
      type: deliveryHeadingType,
      html: deliveryHeadingBody,
      typographyClass: "type-link"
    },
    open: isDeliveryOpen.value
  };

  const personalInfoProps = {
    fields,
    shippingDetails: personalInfoData
      ? [`${personalInfoData?.firstName || ""} ${personalInfoData?.lastName || ""}`, `${genderTypeMapping(personalInfoData?.gender) || ""}`, `${(showSsn && personalInfoData?.idNumber) || ""}`, `${personalInfoData?.phoneArea || ""} ${personalInfoData?.phone || ""}`]
      : [],
    billingDetails: billingData ? [`${billingData?.street || ""} ${billingData?.apartment || ""}`, `${billingData?.postalCode || ""} ${billingData?.city || ""}`, `${billingData?.country || ""}`] : []
  };

  const deliveryProps = {
    fields,
    details: pdpDelivery?.value === "home delivery" && shippingData ? [`${personalInfoData?.firstName || ""} ${personalInfoData?.lastName || ""}`, `${shippingData.street || ""} ${shippingData.apartment || ""}`, `${shippingData.postalCode || ""} ${shippingData.city || ""}`] : []
  };

  const getCountryFromMarket = async () => {
    const country = await getMarketCountry();
    if (country.regionAbbr === "SE" || country.regionAbbr === "IT" || country.regionAbbr === "ES") {
      setShowSsn(true);
    } else {
      setShowSsn(false);
    }
  };
  useEffect(() => {
    getCountryFromMarket();
    if (code) {
      handleError(code);
    }
  }, []);

  const checkStock = async () => {
    if (lid && lno) {
      return true;
    } else {
      const stock = await checkPdpStock({
        lccId: pdpLcc?.id,
        towbarState: pdpSummary?.towbarState,
        modelConfigCode: pdpSummary?.modelConfigCode,
        stocktype: stockTypes[pdpPlanType]
      });
      setPdpStock(stock);
      return stock.length > 0 && stock[0] && stock[0].availableQty > 0;
    }
  };
  const onPayment = (url, payMethodName) => {
    if (payMethodName === "IDeal") {
      global.location.href = url;
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
      setIframeUrl(url);
    }
  };

  useEffect(() => {
    const { currentStep, _barSteps } = formatProgressBarSteps(fields.template.fields.progressBar.fields.steps);
    if ((lid || lno) && pdpRiskCheck !== "R") {
      _barSteps[0].disabled = true;
    }
    setProgressBarData(_barSteps);
    updateProgressBar(currentStep + 1);
    return function cleanUp() {
      setProgressBarData(null);
      updateProgressBar(null);
    };
  }, [pdpRiskCheck]);

  useEffect(() => {
    /** Mesurement */
    if (orderPreview && !measurementIsSent) {
      setMeasurementIsSent(true);
      let dataLayer = window.dataLayer || [];
      let product = getProductByOrderPreview(orderPreview);

      dataLayer.push({
        event: "checkout",
        step: 3,
        checkoutFlow: product?.membershipPlan,
        products: [product]
      });
    }
  }, [orderPreview]);

  const prepareSaveOrderSku = useCallback(
    async (isLoan, saveBody) => {
      let skuQtyVoList = [];
      if (!isLoan) {
        skuQtyVoList = [...pdpSummary.apiBody.skuQtyVoList];
      }
      const deliverySkuIndex = pdpSummary.apiBody.skuQtyVoList.findIndex(x => x.skuId === pdpBundle.delivery.skuId);
      if (pdpDelivery.value === "home delivery") {
        const mccRes = await api.mcc.get(null, {
          lccId: pdpLccId,
          zipCode: shippingData.postalCode
        });
        if (mccRes?.obj && mccRes?.obj.length > 0) {
          Object.assign(saveBody.orderExtraData, {
            mccId: mccRes?.obj[0].id.toString()
          });
        }
        if (deliverySkuIndex === -1) {
          skuQtyVoList.push({
            skuId: pdpBundle.delivery.skuId,
            qty: 1
          });
          pdpSummary.apiBody.skuQtyVoList.push({
            skuId: pdpBundle.delivery.skuId,
            qty: 1
          });
        }
      } else if (pdpTouchPoint) {
        const mccRes = await api.mcc.get(null, {
          lccId: pdpLccId,
          zipCode: pdpTouchPoint.zipCode
        });
        if (mccRes?.obj && mccRes?.obj.length > 0) {
          Object.assign(saveBody.orderExtraData, {
            mccId: mccRes?.obj[0].id.toString()
          });
        }
        if (deliverySkuIndex >= 0) {
          pdpSummary.apiBody.skuQtyVoList.splice(deliverySkuIndex, 1);
        }
        Object.assign(saveBody.orderExtraData, {
          touchPointId: pdpTouchPoint.id.toString()
        });
      }
      Object.assign(saveBody, {
        skuQtyVoList
      });
    },
    [pdpSummary?.apiBody?.skuQtyVoList, pdpBundle, pdpTouchPoint, shippingData, pdpLccId]
  );
  const prepareSaveOrder = async regulationCheck => {
    const country = await getMarketCountry();
    const orderExtraData = {
      lccId: pdpLccId,
      carDeliveryMethod: pdpDelivery.label
    };
    if (!isEmptyObject(regulationCheck)) {
      Object.assign(orderExtraData, { regulationCheck });
    }
    const saveBody = {
      subType: getPdpOrderType(pdpPlanType),
      productType: ProductType.vehicle,
      contactEmail: user?.email,
      contactMobile: user?.mobileNo,
      contactMobileCountryCode: user?.mobileNoCountryCode,
      bundleId: pdpSummary.apiBody.bundleId,
      orderExtraData
    };
    Object.assign(saveBody, {
      billingAddress: {
        billingAddress1: billingData?.street || "",
        billingAddress2: billingData?.apartment || "",
        billingCity: billingData?.city || "",
        billingCountryCode: country.regionAbbr,
        billingCountry: country.regionName,
        billingFirstName: personalInfoData?.firstName || "",
        billingLastName: personalInfoData?.lastName || "",
        billingPhone: personalInfoData?.phone,
        billingPhoneCountryCode: personalInfoData?.phoneArea,
        billingPostalCode: billingData?.postalCode || "",
        billingEmail: user?.email
      }
    });
    if (pdpDelivery.value === "home delivery") {
      Object.assign(saveBody, {
        receiverInfoAddress: {
          receiverAddress1: shippingData.street,
          receiverAddress2: shippingData.apartment,
          receiverCity: shippingData.city,
          receiverCountryCode: country.regionAbbr,
          receiverCountry: country.regionName,
          receiverFirstName: user?.firstName,
          receiverLastName: user?.lastName,
          receiverMobile: user?.mobileNo,
          receiverMobileCountryCode: user?.mobileNoCountryCode,
          receiverPostalCode: shippingData.postalCode,
          receiverEmail: user?.email
        }
      });
    }
    if (relatedOrderNo) {
      Object.assign(saveBody, {
        relatedOrderNo
      });
    }
    return saveBody;
  };
  const prepareFinalizeOrder = async regulationCheck => {
    const saveBody = await prepareSaveOrder(regulationCheck);
    const userIdentity = await getUserIdentity();
    await prepareSaveOrderSku(true, saveBody);
    Object.assign(saveBody, {
      subType: orderTypes.buyACarLoan,
      orderNo: lno,
      billingAddress: {
        billingAddress1: billingData?.street || "",
        billingAddress2: billingData?.apartment || "",
        billingCity: billingData?.city || "",
        billingCountryCode: billingData.countryCode,
        billingCountry: billingData.country,
        billingFirstName: userIdentity?.firstName || "",
        billingLastName: userIdentity?.lastName || "",
        billingPhone: user?.mobileNo,
        billingPhoneCountryCode: user?.mobileNoCountryCode,
        billingPostalCode: billingData?.postalCode || "",
        billingEmail: userIdentity?.email
      }
    });
    return saveBody;
  };
  const saveOrder = async regulationCheck => {
    if (await checkStock()) {
      const body = await prepareSaveOrder(regulationCheck);
      await prepareSaveOrderSku(false, body);
      return await api.vehicle.order.save(null, body);
    }
  };
  const finalizeOrder = async regulationCheck => {
    const body = await prepareFinalizeOrder(regulationCheck);
    await api.vehicle.order.loan.finalize(null, body);
    return {
      orderId: lid,
      orderNo: lno
    };
  };

  const cardInfoProps = {
    fields: cardInfo.fields,
    async onSubmit({ isValid, paymentRegistrationToken, bankCardId, paymentToken, regulationCheck }) {
      const savePayment = webStorage.get(S_GEAR_SAVE_PAYMENT);
      if (isValid && (paymentRegistrationToken || paymentToken)) {
        try {
          if (pdpIsLoan) {
            await finalizeOrder(regulationCheck);
            orderNo = lno;
            orderId = lid;
          } else {
            const { obj } = await saveOrder(regulationCheck);
            orderNo = obj.orderNo;
            orderId = obj.orderId;
          }
          const confirmationUrlQuery = getQuery(confirmationLink.value.href);
          const redirect = decodeURIComponent(confirmationUrlQuery.redirect);
          const returnUrl = decodeURIComponent(
            appendQuery(confirmationLink.value.href, {
              ...confirmationUrlQuery,
              redirect: encodeURIComponent(
                appendQuery(redirect, {
                  orderNo: orderNo,
                  orderId: orderId,
                  pdpIsLoan: pdpIsLoan
                })
              )
            })
          );
          const payBody = {
            orderNo: orderNo, //订单号
            returnUrl: returnUrl
          };
          if (paymentRegistrationToken) {
            Object.assign(payBody, {
              paymentRegistrationToken,
              bankCardId,
              isRemember: 0
            });
          } else {
            Object.assign(payBody, {
              paymentToken,
              isRemember: savePayment ? 1 : 0
            });
          }
          let status, redirectUrl, payMethodName;
          try {
            const { obj } = await api.order.pay(null, payBody);
            status = obj.status;
            redirectUrl = obj.redirectUrl;
            payMethodName = obj.payMethodName;
          } catch (ex) {
            throw ex;
          }
          setPdpRiskCheck(null);

          if (window.history && window.history.pushState) {
            window.history.replaceState("forward", null, `/${lang}/`);
          }
          switch (status) {
            case _3dsStatus.pendingHasLink:
              onPayment && onPayment(redirectUrl, payMethodName);
              break;
            case _3dsStatus.successNoLink:
              onPayment && onPayment(returnUrl, payMethodName);
              break;
            case _3dsStatus.failed:
              await handleError(_3dsStatus.failed);
              break;
            default:
              break;
          }
        } catch (ex) {
          console.log("payment ex", ex);
          if (ex?.code === "PAYMENT_EXCEPTION_007") {
            setPdpRiskCheck("R");
          }
          await handleError(ex?.code);
        }
      }
    }
  };
  if (iframeUrl) {
    return <CheckoutPaymentFrame {...{ iframeUrl }} />;
  } else {
    return (
      <PdpCheckoutTemplate {...templateProps}>
        <div className="start-of-sales-delivery-details">
          <AccordionWrapper {...personalInfoAccordionProps}>
            <PersonalInformationAccordion {...personalInfoProps} />
          </AccordionWrapper>
          <AccordionWrapper {...deliveryAccordionProps}>
            <DeliveryDetailsAccordion {...deliveryProps} />
          </AccordionWrapper>
        </div>
        <PdpCardInfo className="card-info" {...cardInfoProps} />
        <TrustSeals className="trust-seals" images={trustSeals} />
        {errorViewProps.error && <CheckoutErrorView {...errorViewProps} />}
      </PdpCheckoutTemplate>
    );
  }
};
export default PdpPayment;
